<template>
  <div
    class="comment"
    :class="[
      { 'comment--internal': value.isInternal, 'comment--self': isSelf },
      'comment--type-' + ((value.author && value.author.onModel) || 'User').toLowerCase(),
    ]"
  >
    <div class="comment__content">
      <div class="comment__message" v-html="SimpleMarkdownToHtml(value.message)"></div>
      <ButtonComponent
        v-if="$auth.isAuthenticated && !value.isProcessed"
        :icon="['fal', 'check-circle']"
        class="comment__action"
        :promise="promise"
        @click="process"
        :label="$t('markAsProcessed')"
      />
      <slot name="actions" />
      <DropdownComponent
        v-if="value.attachments && value.attachments.length"
        class="comment__attachments"
        :close-on-click="true"
      >
        <ButtonComponent
          :icon="['fal', 'file']"
          :label="`${value.attachments.length} ${$tc('attachment', value.attachments.length).toLowerCase()}`"
          class="text--small"
        />
        <template #dropdown>
          <FileItem
            v-for="(item, index) in value.attachments"
            :key="'attachment_' + index"
            :value="item"
            @click.prevent="download(item)"
            readonly
          />
          <Alert>{{ $tc('externalFileDisclaimer') }}</Alert>
        </template>
      </DropdownComponent>
      <TaskButtonList
        v-if="scope !== 'Task' && $auth.isAuthenticated"
        v-model="value.tasks"
        :reference-id="value.id"
        class="comment__action"
      />
      <DropdownComponent class="comment__action">
        <ButtonComponent :icon="{ icon: ['fal', 'ellipsis-v'], size: 'lg' }" />
        <template #dropdown="{ close }">
          <TaskCreateButton
            v-if="scope !== 'Task' && $auth.isAuthenticated"
            :reference="{ onModel: 'Comment', ref: value.id }"
            @click.native="close"
          />
        </template>
      </DropdownComponent>
    </div>
    <div class="comment__meta">
      <span v-tooltip="{ content: createdAtFormatted.LLL, delay: { show: 400, hide: 100 } }">{{
        createdAtFormatted.fromNow
      }}</span>
      <span v-if="value.isInternal">{{ $t('note') }} ({{ $t('noteVisibilityMessage') }})</span>
      <span v-if="value.author">{{ value.author.name }}</span>
      <span v-if="name">{{ name }}</span>
      <span v-if="value.isProcessed && value.processedBy"
        >{{ $t('processedBy') }} {{ $path('processedBy.name', value) }}</span
      >
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import CommentAPI from '@/api/Comment'
import { DownloadUrl, SimpleMarkdownToHtml, StripHTML } from '@/utils'

import { DropdownComponent } from 'vue-elder-dropdown'
import TaskButtonList from '@/components/Task/ButtonList'
import TaskCreateButton from '@/components/Task/CreateButton'
import { AlertComponent as Alert } from 'vue-elder-alert'
import FileItem from './FileItem.vue'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  inject: ['scope'],
  data() {
    return {
      promise: null,
    }
  },
  computed: {
    name() {
      if (!this.value.scope) return
      return this.value.scope
        .map(s => {
          switch (s.onModel) {
            case 'Project':
              return this.$t('theWholeProject')
            default:
              return s.name
          }
        })
        .join(' / ')
    },
    undoneTasks() {
      if (!this.value || !this.value.tasks) return []
      return this.value.tasks.filter(t => t.status !== 'done')
    },
    createdAtFormatted() {
      if (!this.value.createdAt) return {}
      return {
        LLL: moment(this.value.createdAt).format('LLL'),
        fromNow: moment(this.value.createdAt).fromNow(),
      }
    },
    isSelf() {
      if (!this.$path('value.author.id')) return
      return this.$path('value.author.id') === this.$path('$store.state.user.id')
    },
  },
  methods: {
    process() {
      this.promise = CommentAPI.mutate({ id: this.value.id, isProcessed: true }).then(data => {
        this.value.isProcessed = true
        this.value.processedBy = data.processedBy
      })
    },
    download(file) {
      DownloadUrl(file.url, file.name)
    },
    SimpleMarkdownToHtml(val) {
      return SimpleMarkdownToHtml(StripHTML(val))
    },
  },
  components: {
    DropdownComponent,
    TaskButtonList,
    TaskCreateButton,
    FileItem,
    Alert,
  },
}
</script>

<style lang="scss">
.comment {
  &--type-contact {
    .comment__message {
      border-top-left-radius: 0px;
    }
  }

  &--type-user {
    .comment__content {
      flex-direction: row-reverse;
    }

    .comment__meta {
      text-align: end;
    }

    .comment__message {
      border-top-right-radius: 0px;
    }
  }

  &--self {
    .comment__message {
      background-color: css-darken('primary', 20%);
      color: white;
    }
  }

  &--internal {
    .comment__message {
      background-color: css-lighten('warning', 40%);
      color: css-darken('warning', 35%);
    }
  }

  &__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__message {
    position: relative;
    background-color: $grey;
    padding: 1rem 1.2rem;
    border-radius: 10px;
    white-space: pre-line;
    max-width: 600px;

    &-internal {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-size: 0.8em;
      color: css-darken('warning', 5%);
      margin-top: 5px;
    }

    a {
      text-decoration: underline;
      color: inherit;
    }
  }

  &__attachments {
    .kvass-file__thumbnail {
      cursor: pointer;
    }
    .kvass-file__thumbnail-download {
      display: none;
    }
    .elder-dropdown__content-box {
      gap: 0.25rem;
    }
    .elder-dropdown__content {
      min-width: 300px;
    }
  }

  &__action {
    font-size: 0.8em !important;

    .elder-dropdown__content {
      font-size: 1em;
    }

    .elder-dropdown__trigger {
      .elder-button__icon {
        padding: 0.25rem 0.75rem;
      }
    }
  }

  &__meta {
    opacity: 0.5;
    font-size: 0.8em;
    padding-left: 0.5rem;
    margin-top: 0.5rem;

    & > *:not(:first-child) {
      position: relative;
      margin-left: 0.75rem;

      &::before {
        content: '\00B7';
        position: absolute;
        top: 0;
        left: -0.5rem;
      }
    }
  }
}
</style>
